/**
 * shipmentAdapter — shape API shipment rows into the structure that the
 * workspace preview components consume. Lives outside V2WorkspacePage so unit
 * tests can import the pure functions without bringing Clerk + react-router
 * into the test environment.
 */

import { normaliseYMD } from './preview/dateHelpers'
import { deriveTrackingId } from './preview/helpers'

const STATUS_MAP = {
  PENDING_QUOTE: 'RFQS_SENT',
  QUOTED: 'DECIDE',
  QUOTE_CONFIRMED: 'BOOKED',
  BOOKED: 'BOOKED',
  IN_TRANSIT: 'BOOKED',
  DELIVERED: 'BOOKED',
  DRAFT: 'DRAFT'
}

const QUOTE_STATUS_TO_PROVIDER = {
  PENDING: 'pending',
  RECEIVED: 'received',
  VALID: 'received',
  SELECTED: 'selected',
  CONFIRMED: 'selected',
  DECLINED: 'declined',
  EXPIRED: 'declined'
}

const PROVIDER_STATUS_RANK = { pending: 0, received: 1, selected: 2, declined: 0 }

// Sidebar status groups, in the fixed priority order the column renders them.
// Items needing human help surface first; finished work sinks to the bottom.
export const SIDEBAR_GROUPS = ['action_needed', 'responses_received', 'submitted', 'accepted']

const STATUS_TO_GROUP = {
  DRAFT: 'action_needed',
  NEEDS_REPLY: 'action_needed',
  RFQS_SENT: 'submitted',
  PARTIAL: 'responses_received',
  DECIDE: 'responses_received',
  BOOKED: 'accepted'
}

const TRUCK_TYPE_LABELS = {
  dry_van: 'Dry van',
  reefer: 'Reefer',
  flatbed: 'Flatbed',
  step_deck: 'Step deck',
  hotshot: 'Hotshot'
}

// Build the [label, value] term pairs the comparison card's per-row accordion
// shows under "Terms" (source-verify view). Only includes fields the quote
// actually carries — missing ones are dropped so the accordion never shows a
// label with an empty value.
function buildQuoteTerms(q) {
  const terms = []
  const add = (label, val) => {
    if (val === null || val === undefined) return
    const s = String(val).trim()
    if (s) terms.push([label, s])
  }
  add('Rate type', q.rate_type ?? q.rateType)
  add('Vessel', q.vessel_name ?? q.vesselName)
  add('Voyage', q.voyage_number ?? q.voyageNumber)
  add('Departure', q.departure_date ?? q.departureDate)
  add('Arrival', q.arrival_date ?? q.arrivalDate)
  add('Cutoff', q.cutoff_date ?? q.cutoffDate)
  return terms
}

/**
 * Turn `shipment.quotes` (returned by GET /:id) into the rail's provider rows.
 * Groups by provider and prefers the most-progressed quote when a provider has
 * multiple rows.
 */
export function deriveProviders(quotes) {
  if (!Array.isArray(quotes) || quotes.length === 0) return []
  const byProviderId = new Map()
  for (const q of quotes) {
    const providerId = q.provider_id || q.providerId || q.provider?.id
    if (!providerId) continue
    const name = q.provider_name || q.providerName || q.provider?.name || 'Provider'
    const rawStatus = (q.status || 'PENDING').toUpperCase()
    const cost = q.total_cost ?? q.totalCost ?? q.cost ?? null
    const days = q.transit_days ?? q.transitDays ?? null
    const hasParsedCost = cost !== null && cost !== undefined && String(cost).trim() !== ''
    const status = rawStatus === 'INCOMPLETE' && hasParsedCost
      ? 'received'
      : (QUOTE_STATUS_TO_PROVIDER[rawStatus] || 'pending')
    const existing = byProviderId.get(providerId)
    if (!existing || PROVIDER_STATUS_RANK[status] > PROVIDER_STATUS_RANK[existing.status]) {
      byProviderId.set(providerId, {
        id: providerId,
        name,
        status,
        cost,
        days,
        // Extra fields consumed by ProviderComparisonCard — the rail ignores
        // them. quoteId lets the card's Accept button drive the same
        // handleAcceptQuote flow the inline QuoteCard uses.
        quoteId: q.id || q.quote_id || q.quoteId || null,
        currency: q.currency || null,
        validUntil: q.valid_until || q.validUntil || null,
        receivedAt: q.updated_at || q.updatedAt || q.created_at || q.createdAt || null,
        reliability: q.provider_reliability ?? q.providerReliability ?? null,
        // Optional comparison columns — show as a column when ≥1 provider has a
        // value, "—" otherwise (ProviderComparisonCard).
        fuel: q.fuel_surcharge ?? q.fuelSurcharge ?? null,
        equipment: q.equipment ?? q.truck_type ?? q.truckType ?? q.container_type ?? q.containerType ?? null,
        // Source-verify accordion payload: the carrier's raw reply plus the
        // parsed terms, so dispatch can check extraction against the email.
        rawReply: q.raw_email_text ?? q.rawEmailText ?? q.replySnippet ?? q.reply_snippet ?? null,
        terms: buildQuoteTerms(q),
        // Comparability / outlier signals — surfaced as row flags. The card
        // also computes a below-cluster flag from the costs it has on screen.
        issues: Array.isArray(q.completion_issues ?? q.completionIssues) ? (q.completion_issues ?? q.completionIssues) : [],
        confidence: q.extraction_confidence?.level ?? q.extractionConfidence?.level ?? null,
        ambiguous: !!(q.ambiguous_candidates ?? q.ambiguousCandidates ?? false)
      })
    }
  }
  return Array.from(byProviderId.values())
}

function buildTimeline(raw) {
  const tl = []
  if (raw.created_at) {
    tl.push({
      date: new Date(raw.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      label: 'Created'
    })
  }
  if (raw.status) {
    const label = raw.status.replace(/_/g, ' ').toLowerCase().replace(/^./, c => c.toUpperCase())
    tl.push({ date: '', label, current: true })
  }
  return tl
}

// Drop user_name values that were corrupted by an older bug: clicking the
// title and blurring without typing persisted the auto-name string. The only
// way the stored name could be all dashes/arrows is if it was such a write,
// so we coerce it back to null and let displayName() derive a fresh title.
function sanitizeStoredUserName(value) {
  if (!value || typeof value !== 'string') return null
  const stripped = value.replace(/[\s—–\-→]/g, '')
  return stripped.length === 0 ? null : value
}

function truckingPickupName(raw) {
  if (raw.pickup_address) return raw.pickup_address
  const cityState = [raw.pickup_city, raw.pickup_state].filter(Boolean).join(', ')
  return [cityState, raw.pickup_zip].filter(Boolean).join(' ') || ''
}

// Newer intake flows (chat) persist locations in origin_address_json /
// destination_address_json and leave the legacy origin_port / destination_port
// columns empty. Fall back to the JSON so the rail isn't stuck on "—".
function addressDisplay(addr) {
  if (!addr || typeof addr !== 'object') return ''
  return addr.displayName || addr.city || ''
}

function addressCode(addr) {
  if (!addr || typeof addr !== 'object') return ''
  const cc = (addr.countryCode || '').toUpperCase()
  const city = (addr.city || '').replace(/[^a-zA-Z]/g, '').slice(0, 3).toUpperCase()
  return cc && city ? `${cc}${city}` : ''
}

// "City, ST" for trucking title labels; falls back through freeform strings.
function cityState(city, state, fallback) {
  const cs = [city, state].filter(Boolean).join(', ')
  return cs || fallback || ''
}

function addressCityState(addr) {
  if (!addr || typeof addr !== 'object') return ''
  return cityState(addr.city, addr.stateCode || addr.state, addr.displayName)
}

// Two-letter initials from a display name ("Eric Mogollon" → "EM") or the
// first letter of an email. Distinct from common/avatarInitials, which takes
// a Clerk user object — here we only ever have a stored name/email string.
export function initialsFromName(value) {
  const v = (value || '').trim()
  if (!v) return '?'
  const parts = v.split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  return v[0].toUpperCase()
}

/**
 * Sidebar row derivations for the grouped-by-status column: which group the
 * row files under, the one-line reason shown beneath the title, the
 * origin → destination · equipment title parts, and who initiated the RFQ.
 * Exported for unit tests; adaptShipment folds the result into its output.
 */
export function deriveSidebarFields(raw, { isTrucking, displayStatus, originAddr, destAddr, readyDate }) {
  const group = STATUS_TO_GROUP[displayStatus] || 'submitted'

  const quoteCount = raw.received_quote_count ?? raw.receivedQuoteCount ?? null
  const openQuestionCount = raw.open_carrier_question_count ?? raw.openCarrierQuestionCount ?? 0
  let statusReason
  if (displayStatus === 'NEEDS_REPLY') {
    statusReason = openQuestionCount > 0
      ? `Carrier asked a question${quoteCount ? ` · ${quoteCount} quote${quoteCount === 1 ? '' : 's'} in` : ''}`
      : 'Agent needs a reply'
  } else if (displayStatus === 'DRAFT') {
    if (!readyDate) statusReason = 'Missing: ready date'
    else if (!raw.commodity) statusReason = 'Missing: commodity'
    else statusReason = 'Draft — RFQs not sent'
  } else if (group === 'responses_received') {
    statusReason = quoteCount
      ? `${quoteCount} quote${quoteCount === 1 ? '' : 's'} to review`
      : (displayStatus === 'PARTIAL' ? 'Quotes arriving' : 'Quotes ready to review')
  } else if (group === 'accepted') {
    const provider = raw.confirmed_provider_name || raw.confirmedProviderName
    statusReason = provider ? `Booked with ${provider}` : 'Booked'
  } else {
    statusReason = 'Waiting on providers'
  }

  // Title labels. Domestic: "City, ST → City, ST · Truck type".
  // Ocean: "UN/LOCODE → locode-or-city · Container type".
  let originLabel, destinationLabel, equipmentLabel
  if (isTrucking) {
    originLabel = cityState(raw.pickup_city, raw.pickup_state, raw.pickup_address)
    destinationLabel = addressCityState(destAddr) || raw.destination_address || ''
    equipmentLabel = TRUCK_TYPE_LABELS[raw.truck_type]
      || (raw.truck_type ? raw.truck_type.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase()) : '')
  } else {
    // Legacy rows store ports as "Qingdao, China (CNQIN)" — prefer the locode
    // column, then a parenthesized locode inside the port string, then the raw
    // string, so titles stay compact ("CNQIN → USHOU · 40GP").
    const embeddedLocode = (s) => (/\(([A-Z]{5})\)/.exec(s || '') || [])[1] || ''
    originLabel = raw.origin_locode || embeddedLocode(raw.origin_port) || raw.origin_port || addressDisplay(originAddr) || ''
    destinationLabel = raw.destination_locode || embeddedLocode(raw.destination_port) || raw.destination_port || addressDisplay(destAddr) || ''
    equipmentLabel = raw.container_type
      ? raw.container_type + (raw.container_count > 1 ? ` ×${raw.container_count}` : '')
      : ''
  }

  // Who kicked this off: a platform user (created_by joined to users) or an
  // external trader whose email started the flow. created_by wins — every
  // in-app path stamps it; email-initiated rows have no user to join.
  const creator = raw.created_by_name || raw.created_by_email || raw.createdByName || raw.createdByEmail
  const initiator = creator
    ? { type: 'user', name: creator, initials: initialsFromName(creator) }
    : { type: 'email', address: raw.buyer_email || raw.seller_email || raw.requester_email || raw.requesterEmail || '' }

  return {
    group,
    statusReason,
    originLabel: originLabel || '—',
    destinationLabel: destinationLabel || '—',
    equipmentLabel,
    initiator,
    lastActivityAt: raw.updated_at || raw.updatedAt || raw.created_at || raw.createdAt || null
  }
}

export function adaptShipment(raw, activityItems = []) {
  const isTrucking = raw.quote_type === 'domestic_trucking'
  const truckingOrigin = truckingPickupName(raw)
  const originAddr = raw.origin_address_json || raw.originAddressJson || null
  const destAddr = raw.destination_address_json || raw.destinationAddressJson || null
  const originPort = isTrucking
    ? truckingOrigin
    : (raw.origin_port || addressDisplay(originAddr) || '')
  const destPort = isTrucking
    ? (raw.destination_address || '')
    : (raw.destination_port || addressDisplay(destAddr) || '')
  // MTEU-744: an open carrier ask overrides the status-derived display for
  // in-flight RFQs — the row files under "Action needed" until the question
  // is dealt with, instead of sitting in "Responses received" where the
  // unresolved ask is invisible. Booked/finished shipments keep their
  // status: a question card there is stale, not actionable.
  const baseStatus = STATUS_MAP[raw.status] || raw.status || 'DRAFT'
  const openCarrierQuestions = raw.open_carrier_question_count ?? raw.openCarrierQuestionCount ?? 0
  const displayStatus = openCarrierQuestions > 0 && (baseStatus === 'RFQS_SENT' || baseStatus === 'DECIDE' || baseStatus === 'PARTIAL')
    ? 'NEEDS_REPLY'
    : baseStatus

  // The outbound RFQ email stamps MARTEU-REQ-<first 12 hex of quote_request.id>
  // into the subject; the inbound poller matches on that same token. Anything
  // user-facing (sidebar/header) MUST derive the tracking ID from the QR id, not
  // the shipment id, or the displayed reference won't match the one the carrier
  // sees. Drafts before the first RFQ send have no QR yet → null.
  const latestQrId = raw.latest_quote_request_id || raw.latestQuoteRequestId || null
  const trackingId = latestQrId ? deriveTrackingId(latestQrId) : null

  const readyDate = normaliseYMD(raw.cargo_ready_date)
  const sidebar = deriveSidebarFields(raw, { isTrucking, displayStatus, originAddr, destAddr, readyDate })

  return {
    id: raw.id,
    modeKind: isTrucking ? 'domestic' : 'ocean',
    ...sidebar,
    quoteRequestId: latestQrId,
    trackingId,
    status: displayStatus,
    userName: sanitizeStoredUserName(raw.user_name || raw.userName),
    // The sidebar dot is a *verify-this* signal — only fire when the user
    // has something to look at (quotes ready to compare, carrier question).
    // Status like RFQS_SENT or DRAFT means "in motion, nothing to do yet" and
    // shouldn't pulse a dot on every row.
    needsAttention: displayStatus === 'DECIDE' || displayStatus === 'NEEDS_REPLY',
    createdAt: raw.created_at || raw.createdAt,
    createdBy: raw.created_by_name || raw.created_by_email || '',
    route: {
      origin: {
        code: isTrucking && raw.pickup_city
          ? raw.pickup_city.slice(0, 3).toUpperCase()
          : (raw.origin_port?.slice(0, 5).toUpperCase()
              || addressCode(originAddr)
              || originPort.slice(0, 5).toUpperCase()
              || '—'),
        name: originPort || '—'
      },
      destination: {
        code: raw.destination_port?.slice(0, 5).toUpperCase()
          || addressCode(destAddr)
          || destPort.slice(0, 5).toUpperCase()
          || '—',
        name: destPort || '—'
      }
    },
    // For a trucking row this IS the truckload mode — the column that decides
    // which carriers get the RFQ (v2_067). The rail has always offered
    // "Truck FTL" / "Truck LTL" here; until now the row read container_type,
    // which trucking never sets, so a load always showed a bare "Trucking"
    // however its mode was set.
    mode: isTrucking
      ? (raw.shipment_mode ? `Truck ${raw.shipment_mode}` : 'Trucking')
      : (raw.container_type
          ? `Ocean ${raw.container_type}` + (raw.container_count > 1 ? ` ×${raw.container_count}` : '')
          : '—'),
    cargo: {
      containerType: raw.container_type || null,
      containerCount: raw.container_count || null,
      // Weight is split across two columns: total_weight (kg) for ocean/air/LCL,
      // total_weight_lbs for domestic trucking. Pick the one that matches the
      // shipment mode so the rail doesn't show "not set" for trucking rows.
      totalWeight: isTrucking
        ? (raw.total_weight_lbs != null ? Number(raw.total_weight_lbs) : null)
        : (raw.total_weight != null ? Number(raw.total_weight) : null),
      totalWeightUnit: isTrucking ? 'lb' : 'kg',
      // pallets_count + dimensions_text are the rail's editable scalars
      // (added in migration v2_039). The structured `pallets` /
      // `dimensions_json` JSONB columns remain for per-pallet detail and are
      // intentionally not surfaced here.
      palletCount: raw.pallets_count != null ? Number(raw.pallets_count) : null,
      dims: raw.dimensions_text || null
    },
    commodity: {
      description: raw.commodity || null
    },
    commercial: {
      incoterm: raw.incoterm || null
    },
    readyDate,
    activity: activityItems,
    providers: deriveProviders(raw.quotes),
    timeline: buildTimeline(raw)
  }
}
